import {create} from 'zustand'

import {getTextDirection, isRTLLanguage} from '@progyny/utils/rtl'

interface LanguageState {
  defaultLanguage: string
  isLanguageUpdated: boolean
  isRTL: boolean
  direction: 'ltr' | 'rtl'
  setDefaultLanguage: (language: string) => void
  setIsLanguageUpdated: (status: boolean) => void
  initializeLanguage: (language: string) => void
}

// Helper function to get initial language
const getInitialLanguage = (): string => {
  if (typeof window === 'undefined') return 'en'
  return localStorage.getItem('ui_language') || 'en'
}

// Helper function to calculate RTL state
const calculateRTLState = (language: string) => {
  const isRTL = isRTLLanguage(language)
  const direction = getTextDirection(language)
  return {isRTL, direction}
}

export const useLanguageStore = create<LanguageState>()((set, get) => {
  const initialLanguage = getInitialLanguage()
  const {isRTL, direction} = calculateRTLState(initialLanguage)

  return {
    defaultLanguage: initialLanguage,
    isLanguageUpdated: false,
    isRTL,
    direction,

    setDefaultLanguage: (language: string) => {
      const {isRTL, direction} = calculateRTLState(language)
      set({
        defaultLanguage: language,
        isRTL,
        direction,
      })
      if (typeof window !== 'undefined') {
        localStorage.setItem('ui_language', language)
      }
    },

    setIsLanguageUpdated: (status: boolean) => {
      set({isLanguageUpdated: status})
    },

    initializeLanguage: (language: string) => {
      const currentLanguage = get().defaultLanguage
      if (language !== currentLanguage) {
        get().setDefaultLanguage(language)
      }
    },
  }
})

// Export hook with same name as before for easier migration
export const useLanguageCriteria = () => useLanguageStore()
